/* eslint-disable no-underscore-dangle */
import React, { Component } from 'react';
import { Meteor } from 'meteor/meteor';
import { get, isEqual, isEmpty, isNil } from 'lodash';
import PropTypes from 'prop-types';
import { Bert } from 'meteor/themeteorchef:bert';
import { Alert } from 'react-bootstrap';

import { styleFetchOne } from '../../../api/Styles/style-methods-client';
import getDefaultStyle from '../../../modules/get-default-style';

import handleErrorInClient from '../../../modules/handle-error-in-client';
import methodPromise from '../../../modules/method-promise-wrapper';

import LeafletMap from '../../components/LeafletMap';
import {
  featurePoint2LatLng,
  featureGetZoom,
  latlong2featurePoint,
  featureSetZoom
} from '../../../modules/geojson-utilities';
import * as layerOps from '../../../api/Layers/layer-operations';

let addOnlineBaseMap;
let getMapCenter;
let drawSingleLayer;
let addDefaultBaseMap;

class MapContainerViewStyle extends Component {
  state = {
    clientModulesReady: false,
    isLeafletMapLoaded: get(this, 'layerMapComponent.current.state.isMapLoaded', false),
    isLayerMapLoaded: false,
    hasErrors: false
  };

  layerMapComponent = React.createRef();

  layerMap = null;

  layerToDraw = null;

  componentDidMount() {
    const { clientModulesReady } = this.state;
    if (!clientModulesReady) {
      this.loadClientModules();
    }
  }

  async componentDidUpdate(prevProps) {
    const { clientModulesReady, isLeafletMapLoaded, isLayerMapLoaded } = this.state;
    const { layer, styleId, defaultBaseMap } = this.props;
    const {
      layer: prevLayer,
      styleId: prevStyleId,
      defaultBaseMap: prevDefaultBaseMap
    } = prevProps;
    if (clientModulesReady && isLeafletMapLoaded && !isLayerMapLoaded) {
      this.initializeMap();
    }
    if (
      isLayerMapLoaded &&
      !isEmpty(defaultBaseMap) &&
      !isEqual(prevDefaultBaseMap, defaultBaseMap)
    ) {
      await addDefaultBaseMap({
        defaultBaseMap,
        leafletMap: this.layerMap,
        isUpdate: true
      });
    }
    if (isLayerMapLoaded && (!isEqual(prevLayer, layer) || !isEqual(prevStyleId, styleId))) {
      const style = (await styleFetchOne(styleId)) || getDefaultStyle(layer);
      this.layerToDraw = await drawSingleLayer({
        layer,
        style,
        leafletMap: this.layerMap,
        layerToDraw: this.layerToDraw,
        isUpdate: true,
        isThumbnailMap: true
      });
    }
  }

  componentWillUnmount() {
    const { layer } = this.props;
    if (layerOps.isRealTime(layer) || layerOps.isAnnotationsRealTime(layer)) {
      this.layerToDraw.stop();
    }
  }

  updateMapState = newState => {
    this.setState({ isLeafletMapLoaded: newState });
  };

  updateMapCenter = async () => {
    const { isLayerMapLoaded } = this.state;
    const { layer } = this.props;
    if (isLayerMapLoaded) {
      const currentMapCenter = latlong2featurePoint(this.layerMap.getCenter().wrap());
      featureSetZoom(currentMapCenter, this.layerMap.getZoom());
      try {
        await methodPromise('layers.updateMapLocation', layerOps.getId(layer), currentMapCenter);
        Bert.alert('Layer Updated!', 'success');
      } catch (error) {
        handleErrorInClient(error);
      }
    } else {
      Bert.alert('Layer Not Loaded!', 'warning');
    }
  };

  initializeMap = async () => {
    const { layer, styleId, defaultBaseMap, userControlSetting } = this.props;
    let layerLocation = layerOps.getMapLocation(layer);
    if (isEmpty(layerLocation)) {
      layerLocation = userControlSetting.defaultMapCenter;
    }
    this.layerMap = get(this, 'layerMapComponent.current.leafletMap');
    this.layerMap.setView(
      featurePoint2LatLng(getMapCenter({ location: layerLocation })),
      featureGetZoom(layerLocation)
    );
    if (isEmpty(defaultBaseMap)) {
      addOnlineBaseMap(this.layerMap);
    } else {
      await addDefaultBaseMap({
        defaultBaseMap,
        leafletMap: this.layerMap
      });
    }
    const style = (await styleFetchOne(styleId)) || getDefaultStyle(layer);
    this.layerToDraw = await drawSingleLayer({
      layer,
      style,
      leafletMap: this.layerMap,
      layerToDraw: this.layerToDraw,
      isThumbnailMap: true
    });
    if (isNil(this.layerToDraw)) this.setState({ hasErrors: true });

    if (layerOps.isGeoJSONApiBased(layer)) {
      if (isEqual(layerLocation, userControlSetting.defaultMapCenter)) {
        this.layerMap.fitBounds(this.layerToDraw.getBounds());
      }
    }
    if (
      (layerOps.isRealTime(layer) || layerOps.isAnnotationsRealTime(layer)) &&
      isEqual(layerLocation, userControlSetting.defaultMapCenter)
    ) {
      this.layerToDraw.once('update', () => {
        this.layerMap.fitBounds(this.layerToDraw.getBounds());
      });
    }
    this.setState({ isLayerMapLoaded: true });
  };

  async loadClientModules() {
    if (Meteor.isClient) {
      ({ addOnlineBaseMap, getMapCenter, drawSingleLayer, addDefaultBaseMap } = await import(
        '../../../modules/leaflet-operations'
      ));
      this.setState({ clientModulesReady: true });
    }
  }

  render() {
    const { deployEnv } = this.props;
    const { hasErrors } = this.state;

    if (hasErrors)
      return (
        <Alert variant="warning" className="mt-5">
          No está disponible para este tipo de layer
        </Alert>
      );

    return (
      <div>
        <LeafletMap
          mapId="layerMap"
          headerType="7"
          ref={this.layerMapComponent}
          updateMapState={this.updateMapState}
          deployEnv={deployEnv}
        />
      </div>
    );
  }
}

MapContainerViewStyle.propTypes = {
  defaultBaseMap: PropTypes.object.isRequired,
  deployEnv: PropTypes.string.isRequired,
  layer: PropTypes.object.isRequired,
  styleId: PropTypes.string.isRequired,
  userControlSetting: PropTypes.object.isRequired
};

export default MapContainerViewStyle;
